// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — the pure game state and rules. No DOM, no canvas, no clock of its own: it is
// driven by paint()/tick(), which is what makes the whole thing unit-testable and the
// host-transfer contract provable without a network (a Race just wraps this).
//
// THE COMMITTED ACTION IS THE FILL. A cell is unknown (0), filled (1, correct and locked),
// or marked (2, your free "this is empty" note). Filling a cell that is truly EMPTY is a
// MISTAKE — it snaps to a mark and costs you (in Strikes, a mistake is a strike and the
// third ends the run). So certainty is rewarded: read the numbers, mark what you can rule
// out, and only fill what you have deduced. A masher that fills blindly racks up mistakes;
// a logical solver makes zero. That gap is measured in tests/balance.test.ts.

import type { Mode } from './modes';
import { boardFor, type Board } from './nono';

// ── cell states ────────────────────────────────────────────────────────────────
export const UNKNOWN = 0;
export const FILLED = 1;
export const MARKED = 2;

export type PaintAction = 'fill' | 'mark' | 'unmark';

// ── scoring constants (the sim referees these; the mechanism audit re-derives its own) ──
export const SCORE = {
  fill: 12, // per correctly filled cell
  mistake: 25, // score lost per mistake (floored at 0)
  complete: 150, // Zen: bonus for revealing the picture
  completeStrikes: 260, // Strikes: bonus for surviving to the reveal
  timeCap: 200, // max time bonus at instant solve, decaying to 0 at mode.targetMs
  blitzBase: 80, // Blitz: banked per completed picture
  blitzPerCell: 4, // Blitz: banked per correct cell of a completed picture
  blitzMistakePts: 15, // Blitz: score lost per mistake
  blitzMistakeMs: 2000, // Blitz: clock time lost per mistake
} as const;

export interface GameEvent {
  t: 'fill' | 'mistake' | 'mark' | 'unmark' | 'linedone' | 'complete' | 'deal' | 'over';
  idx?: number;
  run?: number; // fill: length of the contiguous row-run just extended (for pitch)
  line?: 'row' | 'col';
  index?: number; // linedone: which row/col
  won?: boolean; // over
}

export interface GameState {
  readonly mode: Mode;
  readonly seed: number;
  board: Board;
  boardIndex: number; // Blitz stream position; 0 otherwise
  cells: Uint8Array; // player state per cell (UNKNOWN/FILLED/MARKED) for the CURRENT board
  rowFilled: Int32Array; // correctly-filled count per row (current board)
  colFilled: Int32Array;
  rowTotal: Int32Array; // solution filled count per row/col (current board)
  colTotal: Int32Array;
  rowDone: boolean[];
  colDone: boolean[];
  correctFills: number; // on the CURRENT board
  mistakes: number; // total this run
  strikes: number; // total this run (Strikes uses maxStrikes)
  score: number;
  banked: number; // Blitz: pictures completed
  elapsedMs: number;
  over: boolean;
  won: boolean;
  events: GameEvent[];
}

function loadBoard(state: GameState, board: Board): void {
  state.board = board;
  state.cells = new Uint8Array(board.size * board.size);
  state.rowFilled = new Int32Array(board.size);
  state.colFilled = new Int32Array(board.size);
  state.rowTotal = new Int32Array(board.size);
  state.colTotal = new Int32Array(board.size);
  state.rowDone = new Array(board.size).fill(false);
  state.colDone = new Array(board.size).fill(false);
  state.correctFills = 0;
  for (let r = 0; r < board.size; r++) state.rowTotal[r] = board.rowClues[r].reduce((a, b) => a + b, 0);
  for (let c = 0; c < board.size; c++) state.colTotal[c] = board.colClues[c].reduce((a, b) => a + b, 0);
  // an all-empty line is "done" from the start
  for (let r = 0; r < board.size; r++) if (state.rowTotal[r] === 0) state.rowDone[r] = true;
  for (let c = 0; c < board.size; c++) if (state.colTotal[c] === 0) state.colDone[c] = true;
}

export function createGame(mode: Mode, seed: number): GameState {
  const state: GameState = {
    mode,
    seed: seed >>> 0,
    board: null as unknown as Board,
    boardIndex: 0,
    cells: new Uint8Array(0),
    rowFilled: new Int32Array(0),
    colFilled: new Int32Array(0),
    rowTotal: new Int32Array(0),
    colTotal: new Int32Array(0),
    rowDone: [],
    colDone: [],
    correctFills: 0,
    mistakes: 0,
    strikes: 0,
    score: 0,
    banked: 0,
    elapsedMs: 0,
    over: false,
    won: false,
    events: [],
  };
  loadBoard(state, boardFor(mode, seed, 0));
  return state;
}

/** Length of the contiguous filled row-run containing cell (r,c), by the player's fills. */
function rowRunLength(state: GameState, r: number, c: number): number {
  const n = state.board.size;
  let len = 1;
  for (let cc = c - 1; cc >= 0 && state.cells[r * n + cc] === FILLED; cc--) len++;
  for (let cc = c + 1; cc < n && state.cells[r * n + cc] === FILLED; cc++) len++;
  return len;
}

function timeBonus(state: GameState): number {
  const target = state.mode.targetMs;
  if (target <= 0) return 0;
  const frac = (target - state.elapsedMs) / target;
  return Math.max(0, Math.min(SCORE.timeCap, Math.round(frac * SCORE.timeCap)));
}

function endRun(state: GameState, won: boolean): void {
  if (state.over) return;
  state.over = true;
  state.won = won;
  state.events.push({ t: 'over', won });
}

/** A board (the current one) has just been fully revealed. Bank/advance or finish. */
function onBoardComplete(state: GameState): void {
  state.events.push({ t: 'complete' });
  if (state.mode.stream) {
    // Blitz: bank the picture and deal the next one
    state.banked += 1;
    state.score += SCORE.blitzBase + state.correctFills * SCORE.blitzPerCell;
    state.boardIndex += 1;
    loadBoard(state, boardFor(state.mode, state.seed, state.boardIndex));
    state.events.push({ t: 'deal' });
  } else {
    // Zen / Strikes: the run ends on the reveal
    const bonus = state.mode.maxStrikes > 0 ? SCORE.completeStrikes : SCORE.complete;
    state.score += bonus + timeBonus(state);
    endRun(state, true);
  }
}

function registerCorrectFill(state: GameState, idx: number): void {
  const n = state.board.size;
  const r = (idx / n) | 0;
  const c = idx % n;
  state.cells[idx] = FILLED;
  state.correctFills += 1;
  state.score += SCORE.fill;
  state.rowFilled[r] += 1;
  state.colFilled[c] += 1;
  state.events.push({ t: 'fill', idx, run: rowRunLength(state, r, c) });
  if (!state.rowDone[r] && state.rowFilled[r] === state.rowTotal[r]) {
    state.rowDone[r] = true;
    state.events.push({ t: 'linedone', line: 'row', index: r });
  }
  if (!state.colDone[c] && state.colFilled[c] === state.colTotal[c]) {
    state.colDone[c] = true;
    state.events.push({ t: 'linedone', line: 'col', index: c });
  }
  if (state.correctFills === state.board.filledCount) onBoardComplete(state);
}

function registerMistake(state: GameState, idx: number): void {
  state.cells[idx] = MARKED; // we now know it's empty; snap to a mark
  state.mistakes += 1;
  state.strikes += 1;
  state.events.push({ t: 'mistake', idx });
  if (state.mode.stream) {
    state.score = Math.max(0, state.score - SCORE.blitzMistakePts);
    state.elapsedMs += SCORE.blitzMistakeMs;
    if (state.elapsedMs >= state.mode.durationMs) endRun(state, state.banked > 0);
  } else {
    state.score = Math.max(0, state.score - SCORE.mistake);
    if (state.mode.maxStrikes > 0 && state.strikes >= state.mode.maxStrikes) endRun(state, false);
  }
}

/**
 * Apply one paint action to one cell. Returns the drained event list. `fill` only acts on an
 * unknown cell (→ correct fill or mistake); `mark` sets an unknown cell to a note; `unmark`
 * clears a note. Filled cells are locked. The input layer decides which cells a stroke
 * touches and stops a fill-stroke on the first mistake.
 */
export function paint(state: GameState, idx: number, action: PaintAction): GameEvent[] {
  if (state.over) return drainEvents(state);
  if (idx < 0 || idx >= state.cells.length) return drainEvents(state);
  const cell = state.cells[idx];
  if (action === 'fill') {
    if (cell !== UNKNOWN) return drainEvents(state);
    if (state.board.solution[idx] === 1) registerCorrectFill(state, idx);
    else registerMistake(state, idx);
  } else if (action === 'mark') {
    if (cell === UNKNOWN) {
      state.cells[idx] = MARKED;
      state.events.push({ t: 'mark', idx });
    }
  } else {
    // unmark
    if (cell === MARKED) {
      state.cells[idx] = UNKNOWN;
      state.events.push({ t: 'unmark', idx });
    }
  }
  return drainEvents(state);
}

/** A single TAP with the given tool. Fill fills; Mark toggles a note. */
export function tap(state: GameState, idx: number, tool: 'fill' | 'mark'): GameEvent[] {
  if (tool === 'fill') return paint(state, idx, 'fill');
  const marked = state.cells[idx] === MARKED;
  return paint(state, idx, marked ? 'unmark' : 'mark');
}

/** Advance the clock. Ends a Blitz run when its countdown expires. */
export function tick(state: GameState, dtMs: number): GameEvent[] {
  if (state.over) return drainEvents(state);
  state.elapsedMs += Math.max(0, dtMs);
  if (state.mode.clock === 'down' && state.elapsedMs >= state.mode.durationMs) {
    endRun(state, state.banked > 0);
  }
  return drainEvents(state);
}

export function drainEvents(state: GameState): GameEvent[] {
  if (state.events.length === 0) return [];
  const out = state.events;
  state.events = [];
  return out;
}

/** Milliseconds left on a down-clock (Blitz); 0 for up-clock modes. */
export function timeLeftMs(state: GameState): number {
  if (state.mode.clock !== 'down') return 0;
  return Math.max(0, state.mode.durationMs - state.elapsedMs);
}

/** Progress 0..1 on the current board (fraction of true cells filled). */
export function progress(state: GameState): number {
  const total = state.board.filledCount;
  return total === 0 ? 1 : state.correctFills / total;
}
